// PR-I-84: /my-bookings conectado al back real.
// - Layout: PROFILE (registrado en routeLayouts.ts).
// - Datos: GET /api/v1/bookings/summary/ via myBookingsService.
//   Filtro por tipo va server-side (?type=): al cambiar el filtro se re-fetch
//   para proteger la primera carga si el dataset crece.
//   El toggle entre tabs (active/pending/past) es client-side: los 3 buckets
//   vienen en la misma respuesta y no requieren otro fetch.
// - Sin componentes de terceros para tabs/filtros -- consistente con IntroductionPage.

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useLayout } from '../../../_metronic/layout/core'
import BookingCard from './components/BookingCard'
import { getBookingsSummary, filterToQueryParam } from '../../services/myBookingsService'
import { getOrCreateConversation } from '../../services/chatService'
import { BookingBucket, BookingItem, BookingsSummary, BookingTypeFilter } from './models'
import { RootState } from '../../../setup'
import { UserModel } from '../../modules/auth/models/UserModel'

const EMPTY_SUMMARY: BookingsSummary = { active: [], pending: [], past: [] }

interface TabDef {
  id: BookingBucket
  label: string
  emptyMessage: string
}

const TABS: TabDef[] = [
  {
    id: 'active',
    label: 'Active Bookings',
    emptyMessage: "You don't have active bookings yet.",
  },
  {
    id: 'pending',
    label: 'Pending Request',
    emptyMessage: "You don't have pending requests.",
  },
  {
    id: 'past',
    label: 'Past Bookings',
    emptyMessage: "You don't have past bookings yet.",
  },
]

const FILTERS: Array<{ id: BookingTypeFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'experiences', label: 'Experiences' },
  { id: 'introductions', label: 'Introductions' },
  { id: 'expertise', label: 'Expertise' },
]

const MyBookingsPage: FC = () => {
  const { config, setLayout } = useLayout()
  const restoreRef = useRef(config)
  const history = useHistory()

  const [summary, setSummary] = useState<BookingsSummary>(EMPTY_SUMMARY)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<BookingBucket>('active')
  const [typeFilter, setTypeFilter] = useState<BookingTypeFilter>('all')

  // Avatar del member logueado para la card "You" en experience bookings.
  // Se lee de Redux (mismo patrón que RequestIntroductionPage/Header) para
  // evitar un extra fetch — el back de bookings/summary no expone al viewer.
  const viewer = useSelector<RootState, UserModel>(
    ({ auth }) => auth.user as UserModel,
    shallowEqual,
  )
  const viewerAvatar = viewer?.profilePicture || viewer?.pic || null

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: { ...config.header, display: true },
      aside: { ...config.aside, display: true },
      toolbar: { ...config.toolbar, display: false },
      footer: { ...config.footer, display: true },
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-fetch cuando cambia el filtro por tipo (server-side); tab switch
  // no re-fetch porque los 3 buckets vienen en la misma respuesta.
  const fetchSummary = useCallback(async (filter: BookingTypeFilter) => {
    setIsLoading(true)
    setHasError(false)
    const data = await getBookingsSummary(filterToQueryParam(filter))
    if (data) {
      setSummary(data)
    } else {
      setSummary(EMPTY_SUMMARY)
      setHasError(true)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchSummary(typeFilter)
  }, [typeFilter, fetchSummary])

  const visibleItems: BookingItem[] = useMemo(
    () => summary[activeTab] ?? [],
    [summary, activeTab],
  )

  const currentTab = TABS.find((t) => t.id === activeTab) ?? TABS[0]

  const handleOpenDetail = (_item: BookingItem) => {
    // PR-I-85: abrir modal Full Policy / Cancel. En PR-I-84 se dejan como no-op
    // visibles para no bloquear el flujo actual mientras se define la UX de los modales.
  }

  // PR-I-84 (Armando 2026-09-09): VIEW DETAILS y el hero de la experience card
  // llevan al detalle real de la Experience. Solo aplica a type='experience';
  // introduction/expertise conservan la ruta actual (no-op sobre onOpenDetail).
  const handleViewExperience = (item: BookingItem) => {
    if (item.type !== 'experience') return
    const experienceId = item.extra?.experienceId != null
      ? Number(item.extra.experienceId)
      : null
    if (!experienceId) return
    history.push(`/experience/detail/${experienceId}`)
  }

  // PR-I-84: Send Message.
  // - introduction: usa el ID de la IntroductionRequest (item.sourceId).
  // - experience: usa el Booking id como entity_id + experienceDateId cuando esté
  //   disponible (el back exige STATUS_CONFIRMED, por eso el botón se oculta en pending).
  // - expertise: sin branch en el back, la card lo pinta disabled con tooltip.
  const handleSendMessage = async (item: BookingItem) => {
    if (item.type === 'expertise') {
      // safety-net: el botón está disabled en la card, pero si alguien fuerza el click no hacemos request.
      return
    }
    try {
      const experienceDateId =
        item.type === 'experience' && item.extra?.experienceDateId != null
          ? Number(item.extra.experienceDateId)
          : undefined
      const conv = await getOrCreateConversation(item.type, item.sourceId, experienceDateId)
      const uuid = conv?.uuid || conv?.data?.uuid
      if (uuid) {
        history.push(`/messages?uuid=${uuid}`)
      }
    } catch (_e) {
      // handleApiError ya se dispara dentro de chatService; nada extra que hacer aquí.
    }
  }

  return (
    <div className='tap-host-wrapper'>
      <div className='tap-host-container'>
        <div className='tap-host-title-block'>
          <div className='tap-host-title'>My Bookings</div>
        </div>

        <div className='tap-host-section'>
          {/* Tabs */}
          <div className='card'>
            <ul
              className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-bottom-0'
              style={{ flexWrap: 'nowrap', overflow: 'auto' }}
            >
              {TABS.map((tab, index) => (
                <div className='d-flex flex-row cursor-pointer' key={`tab-${tab.id}`}>
                  <li className='nav-item'>
                    <a
                      className={`nav-link text-capitalize ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </a>
                  </li>
                  <li
                    className={`separator-vertical ${index < TABS.length - 1 ? 'd-flex' : 'd-none'}`}
                  >
                    <hr />
                  </li>
                </div>
              ))}
            </ul>
          </div>

          {/* Filter row */}
          <div
            className='nb-booking-filters'
            role='radiogroup'
            aria-label='Filter bookings by type'
          >
            {FILTERS.map((f) => {
              const isSelected = typeFilter === f.id
              return (
                <button
                  type='button'
                  key={f.id}
                  role='radio'
                  aria-checked={isSelected}
                  className={`nb-booking-filter ${isSelected ? 'nb-booking-filter--selected' : ''}`}
                  onClick={() => setTypeFilter(f.id)}
                >
                  <span
                    className={`nb-booking-filter-radio ${
                      isSelected ? 'nb-booking-filter-radio--selected' : ''
                    }`}
                    aria-hidden
                  >
                    {isSelected && <span className='nb-booking-filter-radio-inner' />}
                  </span>
                  <span className='nb-booking-filter-label'>{f.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        <div className='tap-host-footer nb-booking-list-wrapper'>
          {isLoading ? (
            <div className='nb-booking-list nb-booking-list--loading' aria-busy='true'>
              {[0, 1, 2].map((i) => (
                <div key={i} className='nb-booking-card-skeleton' />
              ))}
            </div>
          ) : hasError ? (
            <div className='no-introductions nb-booking-empty' role='alert'>
              We couldn't load your bookings. Please try again in a moment.
            </div>
          ) : visibleItems.length === 0 ? (
            <div className='no-introductions nb-booking-empty'>{currentTab.emptyMessage}</div>
          ) : (
            <div className='introductions-secction-wrapper-list nb-booking-list'>
              {visibleItems.map((item) => (
                <BookingCard
                  key={item.id}
                  item={item}
                  bucket={activeTab}
                  viewerAvatar={viewerAvatar}
                  onOpenDetail={handleOpenDetail}
                  onSendMessage={handleSendMessage}
                  onViewExperience={handleViewExperience}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export { MyBookingsPage }
export default MyBookingsPage
